// (B) Plain(플레인) Input 백엔드 — antd Input 대체. antd Input v5 DOM 이중클래스 미러링(4변형):
//   bare  : <input class="om-input om-input-outlined(+-sm/-lg/-status-error/-disabled)">
//   affix : <span class="om-input-affix-wrapper om-input om-input-outlined(+-sm/-lg/-focused/...)">
//             <span.om-input-prefix> <input.om-input> <span.om-input-suffix>(clear/password/suffix)
//   search: <span class="om-input-group-wrapper ... om-input-search om-input"><span.om-input-wrapper.om-input-group>
//             <affix-wrapper> <span.om-input-group-addon><button.om-input-search-button>
//   textarea: <textarea class="om-input om-textarea ...">(autoSize)
//   → 소비처/전역/ninegqlUI(antd Input 직접 렌더 공유표면) `.om-input*` SCSS(20파일) 무변경 매칭.
//   시각 base 는 styles/omwInput.css(:where 0-특이도). password/clear 아이콘은 antd SVG 인라인(@ant-design/icons 비의존).
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react'
import type { OmwInputProps, OmwInputRef } from '../api/OmwInput'
import { cx } from './cx'
import '../styles/omwInput.css'

const SIZE_SUFFIX = { sm: 'sm', md: '', lg: 'lg' } as const

// antd EyeOutlined / EyeInvisibleOutlined / CloseCircleFilled path 인라인(@ant-design/icons 비의존).
const EYE = [
  'M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z',
]
const EYE_INVISIBLE = [
  'M942.2 486.2Q889.47 375.11 816.7 305l-50.88 50.88C807.31 395.53 843.45 447.4 874.7 512 791.5 684.2 673.4 766 512 766q-72.67 0-133.87-22.38L323 798.75Q408 838 512 838q288.3 0 430.2-300.3a60.29 60.29 0 000-51.5zm-63.57-320.64L836 122.88a8 8 0 00-11.32 0L715.31 232.2Q624.86 186 512 186q-288.3 0-430.2 300.3a60.3 60.3 0 000 51.5q56.69 119.4 136.5 191.41L112.48 835a8 8 0 000 11.31L155.17 889a8 8 0 0011.31 0l712.15-712.12a8 8 0 000-11.32zM149.3 512C232.6 339.8 350.7 258 512 258c54.54 0 104.13 9.36 149.12 28.39l-70.3 70.3a176 176 0 00-238.13 238.13l-83.42 83.42C223.1 637.49 183.3 582.28 149.3 512zm246.7 0a112.11 112.11 0 01146.2-106.69L401.31 546.2A112 112 0 01396 512z',
  'M508 624c-3.46 0-6.87-.16-10.25-.47l-52.82 52.82a176.09 176.09 0 00227.42-227.42l-52.82 52.82c.31 3.38.47 6.79.47 10.25a111.94 111.94 0 01-112 112z',
]
const CLOSE_CIRCLE = [
  'M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64zm127.98 274.82h-.04l-.08.06L512 466.75 384.14 338.88c-.04-.05-.06-.06-.08-.06a.12.12 0 00-.07 0c-.03 0-.05.01-.09.05l-45.02 45.02a.2.2 0 00-.05.09.12.12 0 000 .07v.02a.27.27 0 00.06.06L466.75 512 338.88 639.86c-.05.04-.06.06-.06.08a.12.12 0 000 .07c0 .03.01.05.05.09l45.02 45.02a.2.2 0 00.09.05.12.12 0 00.07 0c.02 0 .04-.01.08-.05L512 557.25l127.86 127.87c.04.04.06.05.08.05a.12.12 0 00.07 0c.03 0 .05-.01.09-.05l45.02-45.02a.2.2 0 00.05-.09.12.12 0 000-.07v-.02a.27.27 0 00-.05-.06L557.25 512l127.87-127.86c.04-.04.05-.06.05-.08a.12.12 0 000-.07c0-.03-.01-.05-.05-.09l-45.02-45.02a.2.2 0 00-.09-.05.12.12 0 00-.07 0z',
]
const SEARCH_PATH = [
  'M909.6 854.5L649.9 594.8C690.2 542.7 712 479 712 412c0-80.2-31.3-155.4-87.9-212.1-56.6-56.7-132-87.9-212.1-87.9s-155.5 31.3-212.1 87.9C143.2 256.5 112 331.8 112 412c0 80.1 31.3 155.5 87.9 212.1C256.5 680.8 331.8 712 412 712c67 0 130.6-21.8 182.7-62l259.7 259.6a8.2 8.2 0 0011.6 0l43.6-43.5a8.2 8.2 0 000-11.6zM570.4 570.4C528 612.7 471.8 636 412 636s-116-23.3-158.4-65.6C211.3 528 188 471.8 188 412s23.3-116.1 65.6-158.4C296 211.3 352.2 188 412 188s116.1 23.2 158.4 65.6S636 352.2 636 412s-23.3 116.1-65.6 158.4z',
]

function Glyph({ paths, cls }: { paths: string[]; cls: string }) {
  return (
    <span role="img" aria-hidden="true" className={cx('anticon', cls)}>
      <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        {paths.map((d) => (
          <path key={d} d={d} />
        ))}
      </svg>
    </span>
  )
}

export const PlainInput = forwardRef<OmwInputRef, OmwInputProps>(function PlainInput(
  {
    id,
    value,
    onChange,
    onChangeEvent,
    placeholder,
    disabled = false,
    invalid = false,
    type = 'text',
    size = 'md',
    suffix,
    prefix,
    allowClear = false,
    autoFocus,
    onKeyDown,
    onPressEnter,
    onBlur,
    onFocus,
    onSearch,
    enterButton,
    onClear,
    multiline = false,
    rows,
    autoSize,
    maxLength,
    className,
    style,
    'data-testid': dataTestid,
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [pwdVisible, setPwdVisible] = useState(false)
  const [focused, setFocused] = useState(false)
  const isControlled = value !== undefined
  const [innerValue, setInnerValue] = useState('')
  const current = isControlled ? (value ?? '') : innerValue

  useImperativeHandle(ref, () => ({
    focus: () => (inputRef.current ?? textareaRef.current)?.focus(),
    select: () => (inputRef.current ?? textareaRef.current)?.select(),
    blur: () => (inputRef.current ?? textareaRef.current)?.blur(),
  }))

  const sizeCls = SIZE_SUFFIX[size]
  const setValue = (v: string) => {
    if (!isControlled) setInnerValue(v)
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(e.target.value)
    onChange?.(e.target.value)
    onChangeEvent?.(e)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onKeyDown?.(e)
    if (e.key === 'Enter') {
      onPressEnter?.(e)
      if (onSearch) onSearch((e.target as HTMLInputElement).value)
    }
  }

  const clear = () => {
    setValue('')
    onChange?.('')
    onClear?.()
    inputRef.current?.focus()
  }

  // autoSize(textarea): 값 변경 시 높이 재계산
  useLayoutEffect(() => {
    const ta = textareaRef.current
    if (!ta || !autoSize) return
    ta.style.height = 'auto'
    const maxRows = typeof autoSize === 'object' ? autoSize.maxRows : undefined
    let h = ta.scrollHeight
    if (maxRows) {
      const line = parseFloat(getComputedStyle(ta).lineHeight) || 20
      h = Math.min(h, maxRows * line + 8)
    }
    ta.style.height = `${h}px`
  }, [current, autoSize])

  const statusCls = invalid ? 'status-error' : ''

  // ---- TextArea ----
  if (multiline) {
    return (
      <textarea
        ref={textareaRef}
        id={id}
        className={cx(
          'om-input om-textarea om-input-outlined',
          sizeCls && `om-input-${sizeCls}`,
          statusCls && `om-input-${statusCls}`,
          disabled && 'om-input-disabled',
          className,
        )}
        value={current}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        maxLength={maxLength}
        autoFocus={autoFocus}
        style={style}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        data-testid={dataTestid}
      />
    )
  }

  const inputType =
    type === 'url' ? 'url' : type === 'password' && !pwdVisible ? 'password' : 'text'
  const showClear = allowClear && current.length > 0 && !disabled
  const isAffix = !!prefix || !!suffix || showClear || type === 'password'

  const innerInput = (
    <input
      ref={inputRef}
      id={id}
      type={inputType}
      className={
        isAffix
          ? 'om-input'
          : cx(
              'om-input om-input-outlined',
              sizeCls && `om-input-${sizeCls}`,
              statusCls && `om-input-${statusCls}`,
              disabled && 'om-input-disabled',
              className,
            )
      }
      value={current}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      autoFocus={autoFocus}
      style={isAffix ? undefined : style}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={(e) => {
        setFocused(false)
        onBlur?.(e)
      }}
      onFocus={(e) => {
        setFocused(true)
        onFocus?.(e)
      }}
      // antd 파리티: data-testid(rest prop)는 affix/search 여부와 무관하게 항상 내부 <input>
      // (실제 폼 컨트롤)에 부착 — affix-wrapper span 은 fillable 아님(E2E .fill 회귀 방지).
      data-testid={dataTestid}
    />
  )

  const suffixNode = (showClear || type === 'password' || suffix) && (
    <span className="om-input-suffix">
      {showClear && (
        <button
          type="button"
          className="om-input-clear-icon"
          onClick={clear}
          tabIndex={-1}
          aria-label="clear"
        >
          <Glyph paths={CLOSE_CIRCLE} cls="anticon-close-circle" />
        </button>
      )}
      {type === 'password' && (
        <span
          className="om-input-password-icon anticon"
          onClick={() => setPwdVisible((v) => !v)}
          role="button"
          tabIndex={-1}
        >
          <Glyph
            paths={pwdVisible ? EYE : EYE_INVISIBLE}
            cls={pwdVisible ? 'anticon-eye' : 'anticon-eye-invisible'}
          />
        </span>
      )}
      {suffix}
    </span>
  )

  const affix = (extraCls?: string) => (
    <span
      className={cx(
        'om-input-affix-wrapper om-input-outlined',
        !extraCls && 'om-input',
        sizeCls && `om-input-affix-wrapper-${sizeCls}`,
        statusCls && `om-input-affix-wrapper-${statusCls}`,
        disabled && 'om-input-affix-wrapper-disabled',
        focused && 'om-input-affix-wrapper-focused',
        !extraCls && className,
        extraCls,
      )}
      style={extraCls ? undefined : style}
    >
      {prefix && <span className="om-input-prefix">{prefix}</span>}
      {innerInput}
      {suffixNode}
    </span>
  )

  // ---- Search ----
  if (onSearch !== undefined || enterButton !== undefined) {
    return (
      <span
        className={cx(
          'om-input-group-wrapper om-input-group-wrapper-outlined om-input-search om-input',
          sizeCls ? `om-input-search-${sizeCls}` : 'om-input-search-middle',
          !!enterButton && 'om-input-search-with-button',
          className,
        )}
        style={style}
      >
        <span className="om-input-wrapper om-input-group">
          {affix('om-input-search-inner')}
          <span className="om-input-group-addon">
            <button
              type="button"
              className="om-btn om-btn-default om-btn-color-primary om-btn-variant-solid om-input-search-button"
              onClick={() => onSearch?.(current)}
              disabled={disabled}
            >
              {enterButton && enterButton !== true ? (
                enterButton
              ) : (
                <Glyph paths={SEARCH_PATH} cls="anticon-search" />
              )}
            </button>
          </span>
        </span>
      </span>
    )
  }

  // ---- Affix (prefix/suffix/allowClear/password) ----
  if (isAffix) return affix()

  // ---- Bare ----
  return innerInput
})
